import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@growtherapy/sprout-ui";
import {
  VisitorDrawerState,
  providerIsOnlineAtom,
  visitorDrawerStateAtom,
  visitorIsHelpModalOpenAtom,
} from "../state";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useEffect } from "react";
import { useRecordProviderNoShowReportCallback } from "./useRecordProviderNoShowReportCallback";
import { useAtom, useAtomValue } from "jotai";
import { useChatDrawerButtonUtils } from "../chat/useChatDrawerButtonUtils";
import { useClearProviderNoShowTabTitleCallback } from "./useClearProviderNoShowTabTitle";
import { isProviderNotHereModalOpenAtom } from "./admission/state";
import { useTranslate } from "@growtherapy/i18n";

export function ProviderNotHereModal({
  isOpen,
  onClose,
  onReportProviderNoShow,
  onMessageProvider,
  isProviderOnline,
}: {
  isOpen: boolean;
  onClose: () => void;
  onReportProviderNoShow: () => void;
  isProviderOnline: boolean;
  onMessageProvider: () => void;
}) {
  const t = useTranslate();
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader>
        {t("telehealth-core:waitingRoom.isYourProviderNotHereLetUsKnow")}
      </ModalHeader>
      <ModalBody aria-live="polite">
        {isProviderOnline
          ? t("telehealth-core:waitingRoom.yourProviderAppearsToBeOnline")
          : t("telehealth-core:waitingRoom.weNoticedYourProviderHasntShownUp")}
      </ModalBody>
      <ModalFooter
        actions={[
          {
            key: "continue-waiting",
            use: "secondary",
            onClick: onClose,
            actionContent: t("telehealth-core:waitingRoom.continueWaiting"),
          },
          {
            key: "report-provider-no-show",
            use: "primary",
            onClick: isProviderOnline
              ? onMessageProvider
              : onReportProviderNoShow,
            actionContent: isProviderOnline
              ? t("telehealth-core:waitingRoom.messageProvider")
              : t("telehealth-core:waitingRoom.yesMyProviderIsntHere"),
          },
        ]}
      />
    </Modal>
  );
}

export function ProviderNotHereModalWrapper({
  onOpenModal,
  onCloseModal,
}: {
  onOpenModal?: () => void;
  onCloseModal?: () => void;
}) {
  const [isProviderNotHereModalOpen, setIsProviderNotHereModalOpen] = useAtom(
    isProviderNotHereModalOpenAtom,
  );

  const { isPastNoShowThreshold, recordProviderNoShowAndRedirect } =
    useRecordProviderNoShowReportCallback();
  const { toggleChatDrawer } = useChatDrawerButtonUtils();
  const clearProviderNoShowTabTitle = useClearProviderNoShowTabTitleCallback();

  const getIsHelpModalOpen = useAtomCallback(
    useCallback((get) => get(visitorIsHelpModalOpenAtom), []),
  );
  const isProviderOnline = useAtomValue(providerIsOnlineAtom);
  const getVisitorDrawerState = useAtomCallback(
    useCallback((get) => get(visitorDrawerStateAtom), []),
  );

  useEffect(() => {
    if (isPastNoShowThreshold && !getIsHelpModalOpen()) {
      setIsProviderNotHereModalOpen(true);
      onOpenModal?.();
    }
  }, [
    isPastNoShowThreshold,
    getIsHelpModalOpen,
    onOpenModal,
    setIsProviderNotHereModalOpen,
  ]);

  return (
    <ProviderNotHereModal
      isOpen={isProviderNotHereModalOpen}
      onClose={() => {
        clearProviderNoShowTabTitle();
        setIsProviderNotHereModalOpen(false);
        onCloseModal?.();
      }}
      onReportProviderNoShow={recordProviderNoShowAndRedirect}
      onMessageProvider={() => {
        clearProviderNoShowTabTitle();
        if (getVisitorDrawerState() !== VisitorDrawerState.CHAT) {
          toggleChatDrawer();
        }
        setIsProviderNotHereModalOpen(false);
      }}
      isProviderOnline={!!isProviderOnline}
    />
  );
}
